// Frontend block-catalog mirror for the diagram editor (§5/§8).
//
// The authoritative catalog is generated backend-side from the flow-dsl source-of-truth
// (lambda/solnstudio/knowledge-pack/buildCatalog.js). This is a small curated mirror for the editor
// palette + client-side validation hints; keep the two in sync when blocks change. The `branches`
// flag marks blocks that emit condition edges instead of an implicit success (the NO_SUCCESS set).

export const NO_SUCCESS_TYPES = [
  'CheckHoursOfOperation', 'CheckMetricData', 'Compare', 'GetParticipantInput',
  'ConnectParticipantWithLexBot', 'DistributeByPercentage', 'Loop', 'TransferContactToQueue',
  'ShowView', 'Wait',
];

export const TERMINAL_TYPES = ['DisconnectParticipant', 'EndFlowExecution', 'EndFlowModuleExecution'];

// Types that hand the contact off and END THE CURRENT FLOW on success — they legitimately have no
// outgoing transition in this flow's graph (any branch they expose is an error path). Treated like
// terminals for the "no outgoing transition" check so they don't raise false flow-check warnings.
export const FLOW_EXIT_TYPES = [...TERMINAL_TYPES, 'TransferToFlow', 'TransferContactToQueue'];

// Blocks that END THE FLOW the moment they run on the SUCCESS path — once the contact is placed into
// the callback queue (CreateCallbackContact), anything wired after it on success never runs. Mirrors
// the backend validateFlowLegality flow-ending-sequencing rule (advisory here — the editor is advisory).
export const FLOW_ENDING_TYPES = ['CreateCallbackContact'];

// Set-attributes blocks whose value map we scan for arithmetic. Amazon Connect can't evaluate
// expressions, so a value like "$.Attributes.x + 1" is invalid (it stores the literal) — the retry-count
// idiom is a Loop block. Mirrors the backend validateFlowLegality rule (advisory here). The leading
// $.-ref anchor keeps E.164 numbers ("+1555…") and hyphenated/path values from false-positiving.
const ATTRIBUTE_SETTING_PARAMS = { UpdateContactAttributes: 'Attributes', UpdateFlowAttributes: 'FlowAttributes' };
const ARITHMETIC_ON_REF = /\$\.[\w.[\]]+\s*[-+*/]\s*(?:\d|\$\.)/;

// --- Amazon Connect flow TYPES (mirror of lambda/solnstudio/flow-dsl/flowTypes.js) ------------------
// The Connect `Type` enum (+ flow module). Keep in sync with the backend flowTypes.js; the backend
// validators are authoritative, this drives the editor's type Select, palette filter, and advisory
// flow-checks.
export const FLOW_TYPES = [
  'CONTACT_FLOW', 'CUSTOMER_QUEUE', 'CUSTOMER_HOLD', 'CUSTOMER_WHISPER', 'AGENT_HOLD', 'AGENT_WHISPER',
  'OUTBOUND_WHISPER', 'AGENT_TRANSFER', 'QUEUE_TRANSFER', 'CAMPAIGN', 'CONTACT_FLOW_MODULE',
];

export const FLOW_TYPE_LABELS = {
  CONTACT_FLOW: 'Contact Flow (Inbound)',
  CUSTOMER_QUEUE: 'Customer Queue Flow',
  CUSTOMER_HOLD: 'Customer Hold Flow',
  CUSTOMER_WHISPER: 'Customer Whisper Flow',
  AGENT_HOLD: 'Agent Hold Flow',
  AGENT_WHISPER: 'Agent Whisper Flow',
  OUTBOUND_WHISPER: 'Outbound Whisper Flow',
  AGENT_TRANSFER: 'Transfer to Agent Flow',
  QUEUE_TRANSFER: 'Transfer to Queue Flow',
  CAMPAIGN: 'Campaign Flow',
  CONTACT_FLOW_MODULE: 'Contact Flow Module',
};

const CONTACT_FLOW_TYPES = FLOW_TYPES.filter((t) => t !== 'CONTACT_FLOW_MODULE');

// Mantine Select data for the editor's flow-type picker (contact flows only — a module's type is fixed).
export const FLOW_TYPE_SELECT_DATA = CONTACT_FLOW_TYPES.map((t) => ({ value: t, label: FLOW_TYPE_LABELS[t] }));

// Per-block flow-type legality — only the RESTRICTED blocks (absent ⇒ valid in all types). Mirrors
// backend flowTypes.BLOCK_FLOW_TYPE_RULES. Phase-4 calibration: DisconnectParticipant and
// TransferParticipantToThirdParty are legal in ALL flow types INCLUDING modules (real modules hang up /
// dial out on terminal branches), so they are intentionally NOT restricted here; only whole-contact
// resume/redirect blocks (EndFlowExecution, TransferToFlow, TransferContactToQueue) stay module-illegal.
const BLOCK_FLOW_TYPE_RULES = {
  EndFlowModuleExecution: ['CONTACT_FLOW_MODULE'],
  EndFlowExecution: CONTACT_FLOW_TYPES,
  TransferToFlow: CONTACT_FLOW_TYPES,
  TransferContactToQueue: ['CONTACT_FLOW', 'CUSTOMER_QUEUE', 'AGENT_TRANSFER', 'QUEUE_TRANSFER'],
  CreateCallbackContact: ['CONTACT_FLOW', 'CUSTOMER_QUEUE'],
};

/** True if `blockType` is permitted in `flowType` (unrestricted blocks are permitted everywhere). */
export function allowedInFlowType(blockType, flowType) {
  const rule = BLOCK_FLOW_TYPE_RULES[blockType];
  if (!rule) return true;
  return rule.includes(flowType);
}

/** Resolve a flow's Connect Type on read (prefers explicit flowType; derives the module default). */
export function resolveFlowType(flow) {
  if (!flow) return 'CONTACT_FLOW';
  const explicit = flow.flowType || (flow.dsl && flow.dsl.flowType);
  if (explicit && FLOW_TYPES.includes(explicit)) return explicit;
  const kind = (flow.dsl && flow.dsl.kind) || flow.type || flow.kind;
  if (kind === 'flow_module' || kind === 'module') return 'CONTACT_FLOW_MODULE';
  return 'CONTACT_FLOW';
}

export function isModuleFlowType(flowType) { return flowType === 'CONTACT_FLOW_MODULE'; }

// A FLOW_PLAN entry reaches the UI from two sources — the plan-only job result and the persisted
// FLOW_PLAN artifact — and carries BOTH a coarse `type` ('contact_flow' | 'flow_module') and the
// specific Connect `flowType` (CONTACT_FLOW / CUSTOMER_QUEUE / … / CONTACT_FLOW_MODULE), which can
// disagree or be missing. Normalize to ONE canonical shape so consumers never guess: `flowType` is
// always a valid Connect type (derived via resolveFlowType), `type` is kept consistent with it, and
// `isModule` is precomputed. Idempotent; preserves every other field.
export function normalizePlanEntry(e) {
  if (!e || typeof e !== 'object') return e;
  const flowType = resolveFlowType(e);
  const isModule = isModuleFlowType(flowType);
  return { ...e, flowType, type: isModule ? 'flow_module' : 'contact_flow', isModule };
}

export function normalizePlanEntries(entries) {
  return Array.isArray(entries) ? entries.map(normalizePlanEntry) : entries;
}

/** The Add-block PALETTE filtered to the blocks legal in `flowType` (empty groups dropped). */
export function paletteFor(flowType) {
  const ft = flowType || 'CONTACT_FLOW';
  return PALETTE
    .map((grp) => ({ ...grp, items: grp.items.filter((it) => allowedInFlowType(it.type, ft)) }))
    .filter((grp) => grp.items.length);
}

// Curated node display metadata for the editor (friendly name + icon + category color), mirroring the
// relevant slice of the AWS viewer's contactFlowReactFlowParser (getBlockCategory / getActionIcon /
// getActionLabel). Category drives the node's accent color in DiagramEditor's custom node.
const NODE_META = {
  MessageParticipant:           { label: 'Play Message',      icon: '💬', category: 'communication' },
  MessageParticipantIteratively:{ label: 'Loop Prompts',      icon: '🔁', category: 'communication' },
  GetParticipantInput:          { label: 'Get Input',         icon: '⌨️', category: 'input' },
  ConnectParticipantWithLexBot: { label: 'Lex Bot',           icon: '🤖', category: 'input' },
  ShowView:                     { label: 'Show View',         icon: '🖥️', category: 'communication' },
  Compare:                      { label: 'Check Attributes',  icon: '⚖️', category: 'validation' },
  CheckHoursOfOperation:        { label: 'Check Hours',       icon: '🕐', category: 'validation' },
  CheckMetricData:              { label: 'Check Queue Status',icon: '📊', category: 'validation' },
  GetMetricData:                { label: 'Get Queue Metrics', icon: '📈', category: 'validation' },
  EvaluateDataTableValues:      { label: 'Evaluate Data Table', icon: '📒', category: 'integration' },
  DistributeByPercentage:       { label: 'Distribute %',      icon: '🎲', category: 'validation' },
  Loop:                         { label: 'Loop',              icon: '🔂', category: 'validation' },
  Wait:                         { label: 'Wait',              icon: '⏱️', category: 'validation' },
  InvokeLambdaFunction:         { label: 'Invoke Lambda',     icon: '🔗', category: 'integration' },
  InvokeFlowModule:             { label: 'Invoke Module',     icon: '📁', category: 'integration' },
  GetCustomerProfile:           { label: 'Get Customer Profile', icon: '👤', category: 'integration' },
  AssociateContactToCustomerProfile: { label: 'Associate Profile', icon: '👥', category: 'integration' },
  UpdateContactAttributes:      { label: 'Set Attributes',    icon: '🏷️', category: 'data' },
  UpdateContactData:            { label: 'Set Contact Data',  icon: '🌐', category: 'data' },
  UpdateFlowAttributes:         { label: 'Set Flow Attributes', icon: '🧷', category: 'data' },
  UpdateFlowLoggingBehavior:    { label: 'Set Logging',       icon: '📝', category: 'data' },
  TagContact:                   { label: 'Tag Contact',       icon: '🔖', category: 'data' },
  UpdateContactCallbackNumber:  { label: 'Set Callback Number', icon: '☎️', category: 'routing' },
  UpdateContactTargetQueue:     { label: 'Set Queue',         icon: '🚥', category: 'routing' },
  UpdateContactTextToSpeechVoice:{ label: 'Set Voice',        icon: '🗣️', category: 'data' },
  UpdateContactRecordingBehavior:{ label: 'Set Recording',    icon: '⏺️', category: 'data' },
  UpdateContactRecordingAndAnalyticsBehavior: { label: 'Set Recording', icon: '⏺️', category: 'data' },
  UpdateContactEventHooks:      { label: 'Set Hold/Whisper Flow', icon: '🪝', category: 'data' },
  UpdatePreviousContactParticipantState: { label: 'Hold Customer/Agent', icon: '⏸️', category: 'data' },
  UpdateContactRoutingBehavior: { label: 'Change Routing Priority', icon: '⬆️', category: 'routing' },
  CreateCallbackContact:        { label: 'Create Callback',   icon: '↩️', category: 'routing' },
  TransferContactToQueue:       { label: 'Transfer to Queue', icon: '📞', category: 'routing' },
  TransferToFlow:               { label: 'Transfer to Flow',  icon: '🔄', category: 'routing' },
  TransferParticipantToThirdParty: { label: 'Transfer to Phone', icon: '📲', category: 'routing' },
  DisconnectParticipant:        { label: 'Disconnect',        icon: '📵', category: 'terminator' },
  EndFlowExecution:             { label: 'End Flow',          icon: '🏁', category: 'terminator' },
  EndFlowModuleExecution:       { label: 'End Module',        icon: '🏁', category: 'terminator' },
};

// Accent color per category (Mantine palette names) for the custom node's left border / chip.
export const CATEGORY_COLOR = {
  communication: '#1c7ed6', // blue
  input: '#7048e8',         // violet
  validation: '#f08c00',    // orange
  integration: '#0ca678',   // teal
  routing: '#e8590c',       // deep orange
  data: '#5f3dc4',          // indigo
  terminator: '#868e96',    // gray
  general: '#495057',
};

/** Display metadata for a node type: { label, icon, category }. Unknown types degrade gracefully. */
export function nodeMeta(type) {
  return NODE_META[type] || { label: type, icon: '⚙️', category: 'general' };
}

// One-line purpose per block type — sourced from the same wording as the backend catalog
// (knowledge-pack/buildCatalog.js CURATED[type].purpose) and the AWS viewer's getActionDescription.
// Shown as a faint sub-line on the editor node so a generated block says what it DOES, not just its type.
const NODE_DESCRIPTION = {
  MessageParticipant:           'Plays a prompt/message to the caller.',
  MessageParticipantIteratively:'Loops a rotating set of prompts (hold/queue audio).',
  GetParticipantInput:          'Collects DTMF/voice input and branches on it.',
  ConnectParticipantWithLexBot: 'Hands the caller to a Lex V2 bot; branches on intent.',
  ShowView:                     'Shows a step-by-step guide view (chat/agent UI).',
  Compare:                      'Branches on a contact attribute against conditions.',
  CheckHoursOfOperation:        'Branches on whether inside the hours of operation.',
  CheckMetricData:              'Branches on a queue/agent metric threshold.',
  GetMetricData:                'Fetches a queue/contact metric (e.g. EWT) into an attribute.',
  EvaluateDataTableValues:      'Reads values from an Amazon Connect data table.',
  DistributeByPercentage:       'Splits traffic randomly by percentage.',
  Loop:                         'Loops a fixed number of times.',
  Wait:                         'Pauses the flow for a duration or event.',
  InvokeLambdaFunction:         'Calls a Lambda for business logic / integration.',
  InvokeFlowModule:             'Invokes a reusable flow module.',
  GetCustomerProfile:           'Looks up a customer profile (Customer Profiles).',
  AssociateContactToCustomerProfile: 'Associates the contact with a matched customer profile.',
  UpdateContactAttributes:      'Sets contact attributes.',
  UpdateContactData:            'Sets contact-level data such as the language/locale.',
  UpdateFlowAttributes:         'Sets flow-scoped attributes (only visible within this flow).',
  UpdateFlowLoggingBehavior:    'Enables or disables flow logging.',
  TagContact:                   'Attaches tags (key/value) to the contact.',
  UpdateContactCallbackNumber:  'Sets/updates the number used for a queued callback.',
  UpdateContactTargetQueue:     'Sets the working queue for the contact.',
  UpdateContactTextToSpeechVoice: 'Sets the text-to-speech voice/engine.',
  UpdateContactRecordingBehavior: 'Starts/stops recording & analytics.',
  UpdateContactRecordingAndAnalyticsBehavior: 'Starts/stops recording & analytics.',
  UpdateContactEventHooks:      'Associates hold / whisper / customer-queue sub-flows with the contact.',
  UpdatePreviousContactParticipantState: 'Places the customer or agent on or off hold.',
  UpdateContactRoutingBehavior: 'Adjusts the contact’s queue priority or age.',
  CreateCallbackContact:        'Offers/queues a callback.',
  TransferToFlow:               'Transfers to another contact flow.',
  TransferContactToQueue:       'Places the contact into the working queue.',
  TransferParticipantToThirdParty: 'Dials an external phone number / third party.',
  DisconnectParticipant:        'Ends the contact.',
  EndFlowExecution:             'Ends the flow without disconnecting.',
  EndFlowModuleExecution:       'Returns from a flow module.',
};

/** One-line purpose for a block type (faint sub-line on the node). Empty for unknown types. */
export function nodeDescription(type) {
  return NODE_DESCRIPTION[type] || '';
}

function truncate(s, n = 32) {
  if (s == null) return s;
  const str = String(s);
  return str.length > n ? `${str.slice(0, n - 1)}…` : str;
}

/** Last path segment of a Lambda ARN (or the raw ref if it isn't an ARN). */
function lambdaName(arn) {
  if (!arn) return '';
  const parts = String(arn).split(':');
  return parts[parts.length - 1] || String(arn);
}

/**
 * Derive the friendly, NON-AUTHORITATIVE accessors (`prompt`, `lambdaRef`, …) from a node's raw
 * Connect `parameters`. Frontend mirror of lambda/solnstudio/flow-dsl/flowToDsl.js::deriveProps, so
 * GENERATED flows (which carry only `parameters`, never `props`) render with real detail in the editor.
 * `parameters` stays authoritative; this is display-only and never written back into the DSL.
 */
export function deriveProps(node) {
  const p = node?.data?.parameters || node?.parameters || {};
  const props = {};
  if (typeof p.Text === 'string') props.prompt = p.Text;
  else if (typeof p.SSML === 'string') props.prompt = p.SSML;
  if (p.LambdaFunctionARN) props.lambdaRef = p.LambdaFunctionARN;
  if (p.QueueId) props.queueRef = p.QueueId;
  if (p.ContactFlowId) props.flowRef = p.ContactFlowId;
  if (p.FlowModuleId) props.moduleRef = p.FlowModuleId;
  if (p.ComparisonValue) props.compareValue = p.ComparisonValue;
  if (p.LexV2Bot && p.LexV2Bot.AliasArn) props.lexBotRef = p.LexV2Bot.AliasArn;
  // Attributes — not in the backend mirror; UpdateContactAttributes is one of the empty blocks.
  if (p.Attributes && typeof p.Attributes === 'object') {
    const keys = Object.keys(p.Attributes);
    if (keys.length) { props.attributeCount = keys.length; props.attributeKeys = keys; }
  }
  return props;
}

/** Resolve a node's display props: prefer authored `props`, else derive from `parameters`. */
function resolveProps(node) {
  const existing = node?.data?.props || node?.props;
  if (existing && Object.keys(existing).length) return existing;
  return deriveProps(node);
}

/**
 * Ordered key-param rows ({ label, value }) for the node card's compact params block — the important
 * parameters of the block, derived from props. Empty when there's nothing meaningful to show.
 */
export function nodeDetail(node) {
  const type = node?.data?.type || node?.type;
  const props = resolveProps(node);
  const rows = [];
  switch (type) {
    case 'InvokeFlowModule':
      if (props.moduleRef) rows.push({ label: 'Module', value: truncate(props.moduleRef) });
      break;
    case 'TransferToFlow':
      if (props.flowRef) rows.push({ label: 'Flow', value: truncate(props.flowRef) });
      break;
    case 'InvokeLambdaFunction':
      if (props.lambdaRef) rows.push({ label: 'Function', value: truncate(lambdaName(props.lambdaRef)) });
      break;
    case 'UpdateContactTargetQueue':
    case 'TransferContactToQueue':
      if (props.queueRef) rows.push({ label: 'Queue', value: truncate(props.queueRef) });
      break;
    case 'Compare':
      if (props.compareValue) rows.push({ label: 'Value', value: truncate(props.compareValue) });
      break;
    case 'ConnectParticipantWithLexBot':
      if (props.lexBotRef) rows.push({ label: 'Bot', value: truncate(lambdaName(props.lexBotRef)) });
      if (props.prompt) rows.push({ label: 'Prompt', value: truncate(props.prompt, 40) });
      break;
    case 'MessageParticipant':
    case 'GetParticipantInput':
      if (props.prompt) rows.push({ label: 'Prompt', value: truncate(props.prompt, 40) });
      break;
    case 'UpdateContactAttributes':
      if (props.attributeCount) {
        rows.push({ label: 'Sets', value: `${props.attributeCount} attribute${props.attributeCount !== 1 ? 's' : ''}` });
        if (props.attributeKeys) rows.push({ label: 'Keys', value: truncate(props.attributeKeys.join(', '), 40) });
      }
      break;
    default:
      break;
  }
  return rows;
}

/**
 * Friendly, human-readable node label from a DSL node — derives from props when present (Lambda ref,
 * queue/flow ref, prompt snippet) and otherwise falls back to the type's curated name. Mirrors the AWS
 * viewer's getActionLabel intent without the instance-export lookups (the editor has only refs).
 */
export function friendlyLabel(node) {
  const type = node?.data?.type || node?.type;
  // Prefer authored props; fall back to deriving from raw `parameters` (generated flows have no props).
  const props = resolveProps(node);
  const rawLabel = node?.data?.label ?? node?.label;
  const base = nodeMeta(type).label;
  // Respect an engineer-set custom label (anything that isn't just the raw type or the default name).
  if (rawLabel && rawLabel !== type && rawLabel !== base) return rawLabel;
  const snip = (s, n = 28) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s);
  switch (type) {
    case 'InvokeLambdaFunction': return props.lambdaRef ? `Lambda: ${snip(lambdaName(props.lambdaRef))}` : base;
    case 'InvokeFlowModule':     return props.moduleRef ? `Module: ${snip(props.moduleRef)}` : base;
    case 'TransferToFlow':       return props.flowRef ? `Transfer: ${snip(props.flowRef)}` : base;
    case 'UpdateContactTargetQueue':
    case 'TransferContactToQueue': return props.queueRef ? `Queue: ${snip(props.queueRef)}` : base;
    case 'Compare':              return props.compareValue ? `Compare: ${snip(props.compareValue)}` : base;
    case 'UpdateContactAttributes':
      return props.attributeCount ? `Set ${props.attributeCount} Attribute${props.attributeCount !== 1 ? 's' : ''}` : base;
    case 'MessageParticipant':
    case 'GetParticipantInput':
    case 'ConnectParticipantWithLexBot':
      return props.prompt ? snip(props.prompt, 40) : base;
    default: return base;
  }
}

// Palette grouped for the editor. label = friendly; type = Connect action type. The full set is shown
// here; the editor filters it per flow type via paletteFor() (e.g. a module hides contact-ending blocks).
export const PALETTE = [
  { group: 'Interact', items: [
    { type: 'MessageParticipant', label: 'Play Message' },
    { type: 'MessageParticipantIteratively', label: 'Loop Prompts' },
    { type: 'GetParticipantInput', label: 'Get Input' },
    { type: 'ConnectParticipantWithLexBot', label: 'Lex Bot' },
    { type: 'ShowView', label: 'Show View' },
  ]},
  { group: 'Logic', items: [
    { type: 'Compare', label: 'Check Attributes' },
    { type: 'CheckHoursOfOperation', label: 'Check Hours' },
    { type: 'CheckMetricData', label: 'Check Queue Status' },
    { type: 'GetMetricData', label: 'Get Queue Metrics' },
    { type: 'DistributeByPercentage', label: 'Distribute %' },
    { type: 'Loop', label: 'Loop' },
    { type: 'Wait', label: 'Wait' },
  ]},
  { group: 'Integrate', items: [
    { type: 'InvokeLambdaFunction', label: 'Invoke Lambda' },
    { type: 'InvokeFlowModule', label: 'Invoke Module' },
    { type: 'EvaluateDataTableValues', label: 'Evaluate Data Table' },
    { type: 'GetCustomerProfile', label: 'Get Customer Profile' },
    { type: 'AssociateContactToCustomerProfile', label: 'Associate Profile' },
  ]},
  { group: 'Set', items: [
    { type: 'UpdateContactAttributes', label: 'Set Attributes' },
    { type: 'UpdateContactData', label: 'Set Contact Data' },
    { type: 'UpdateFlowAttributes', label: 'Set Flow Attributes' },
    { type: 'UpdateContactTargetQueue', label: 'Set Queue' },
    { type: 'UpdateContactTextToSpeechVoice', label: 'Set Voice' },
    { type: 'UpdateContactRecordingBehavior', label: 'Set Recording' },
    { type: 'UpdateContactRecordingAndAnalyticsBehavior', label: 'Set Recording & Analytics' },
    { type: 'UpdateContactEventHooks', label: 'Set Hold/Whisper Flow' },
    { type: 'UpdatePreviousContactParticipantState', label: 'Hold Customer/Agent' },
    { type: 'UpdateContactRoutingBehavior', label: 'Change Routing Priority' },
    { type: 'UpdateFlowLoggingBehavior', label: 'Set Logging' },
    { type: 'TagContact', label: 'Tag Contact' },
  ]},
  { group: 'Route', items: [
    { type: 'CreateCallbackContact', label: 'Create Callback' },
    { type: 'UpdateContactCallbackNumber', label: 'Set Callback Number' },
    { type: 'TransferContactToQueue', label: 'Transfer to Queue' },
    { type: 'TransferToFlow', label: 'Transfer to Flow' },
    { type: 'TransferParticipantToThirdParty', label: 'Transfer to Phone' },
  ]},
  { group: 'End', items: [
    { type: 'DisconnectParticipant', label: 'Disconnect' },
    { type: 'EndFlowExecution', label: 'End Flow' },
    { type: 'EndFlowModuleExecution', label: 'End Module' },
  ]},
];

export function branches(type) { return NO_SUCCESS_TYPES.includes(type); }
export function isTerminal(type) { return TERMINAL_TYPES.includes(type); }

/** A node legitimately ends the flow (hard terminal or hands the contact off on success). */
export function isFlowExit(type) { return FLOW_EXIT_TYPES.includes(type); }

/** Lightweight client-side graph check mirroring validateFlowGraph (resolved targets, reachability). */
export function validateGraph(dsl) {
  const errors = new Set();
  const warnings = new Set();
  const ids = new Set((dsl.nodes || []).map((n) => n.id));
  const labelOf = (n) => friendlyLabel(n) || n.label || n.id;
  if (!dsl.start) errors.add('No start node set.');
  else if (!ids.has(dsl.start)) errors.add('Start node does not exist.');

  const out = new Map();
  const labelById = new Map((dsl.nodes || []).map((n) => [n.id, friendlyLabel(n) || n.label || n.id]));
  for (const e of dsl.edges || []) {
    if (e.to == null || e.to === '') {
      // A branch with no target — the generated/edited graph left it unconnected (mirrors the backend
      // validateFlowGraph null-target error). Friendlier than the literal "unknown node null".
      errors.add(`"${labelById.get(e.from) || e.from}" has an unconnected branch (${e.label || e.kind || 'edge'}).`);
    } else if (!ids.has(e.to)) {
      errors.add(`Edge points to an unknown node "${e.to}".`);
    }
    out.set(e.from, (out.get(e.from) || 0) + 1);
  }
  for (const n of dsl.nodes || []) {
    if (!isFlowExit(n.type) && !(out.get(n.id) > 0)) warnings.add(`"${labelOf(n)}" has no outgoing transition.`);
  }
  // Reachability from start.
  if (dsl.start && ids.has(dsl.start)) {
    const adj = new Map();
    for (const e of dsl.edges || []) { if (!adj.has(e.from)) adj.set(e.from, []); adj.get(e.from).push(e.to); }
    const seen = new Set([dsl.start]); const stack = [dsl.start];
    while (stack.length) for (const t of adj.get(stack.pop()) || []) if (!seen.has(t)) { seen.add(t); stack.push(t); }
    for (const n of dsl.nodes || []) if (!seen.has(n.id)) warnings.add(`"${labelOf(n)}" is unreachable from start.`);
  }

  // Flow-type legality (advisory) — mirrors backend validateFlowLegality, so the editor flags a block
  // that won't import for this flow's type (e.g. Transfer to Queue inside a Flow Module) as a friendly
  // warning rather than letting it silently ship.
  const flowType = resolveFlowType({ dsl });
  const isModule = isModuleFlowType(flowType);
  let hasReturn = false;
  for (const n of dsl.nodes || []) {
    if (n.type === 'EndFlowModuleExecution') hasReturn = true;
    // Arithmetic-on-attribute guard (runs for every block type — these are legal in all flow types, so
    // it must precede the allowedInFlowType early-continue below).
    const attrParam = ATTRIBUTE_SETTING_PARAMS[n.type];
    if (attrParam && n.parameters && n.parameters[attrParam] != null
        && ARITHMETIC_ON_REF.test(JSON.stringify(n.parameters[attrParam]))) {
      warnings.add(`"${nodeMeta(n.type).label}" does arithmetic on an attribute — Connect can't compute in Set attributes. Use a Loop block to count retries.`);
    }
    if (allowedInFlowType(n.type, flowType)) continue;
    const name = nodeMeta(n.type).label;
    if (isModule) warnings.add(`"${name}" can't be used in a Flow Module — modules Return instead of ending/redirecting the contact.`);
    else if (n.type === 'EndFlowModuleExecution') warnings.add(`"${name}" is only valid in a Flow Module — end a ${FLOW_TYPE_LABELS[flowType] || 'flow'} with Disconnect or End Flow.`);
    else warnings.add(`"${name}" isn't a valid block in a ${FLOW_TYPE_LABELS[flowType] || flowType}.`);
  }
  if (isModule && (dsl.nodes || []).length && !hasReturn) {
    warnings.add('A Flow Module should Return via an "End Module" block.');
  }

  // Flow-ending-block sequencing (advisory) — a flow-ending block's SUCCESS path (default/condition
  // edges, not error branches) should lead straight to a terminal; anything else never runs. Mirrors
  // the backend validateFlowLegality rule (e.g. a prompt placed after Create Callback).
  const typeById = new Map((dsl.nodes || []).map((n) => [n.id, n.type]));
  for (const e of dsl.edges || []) {
    if (!e || e.kind === 'error') continue;
    if (!FLOW_ENDING_TYPES.includes(typeById.get(e.from))) continue;
    const targetType = typeById.get(e.to);
    if (targetType && !TERMINAL_TYPES.includes(targetType)) {
      warnings.add(`"${nodeMeta(typeById.get(e.from)).label}" ends the flow — "${labelById.get(e.to) || e.to}" after it on the success path never runs. Play any confirmation before it, then end with Disconnect.`);
    }
  }
  return { ok: errors.size === 0, errors: [...errors], warnings: [...warnings] };
}
